import { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { CATEGORY_ORDER, useCategoryLabels } from '@/lib/category-labels';
import { Squiggle } from '../primitives';
import { NgoCategoryRow } from './ngo-category-row';
import { NgoFiltersBar } from './ngo-filters';
import { NgoProjectsModal } from './ngo-projects-modal';
import { EMPTY_FILTERS, usePartnerNgos, type NgoFilters, type PartnerNgo } from './use-partner-ngos';

/** The folder illustration over two lines of copy — the shape both empty states share. */
function DirectoryNotice(props: { title: string; body: string }) {
	return (
		<div className="flex flex-col items-center gap-4 py-16 text-center">
			<img
				src="/redesign/partner-ngos/illo-folder.svg"
				alt=""
				aria-hidden
				className="h-auto w-[230px]"
				width={230}
				height={183}
			/>
			<div className="flex flex-col gap-1">
				<p className="text-xl leading-[1.3] font-medium">{props.title}</p>
				<p className="text-ink/60 text-sm leading-[1.5]">{props.body}</p>
			</div>
		</div>
	);
}

/** What the hand-off draws when a search or filter combination matches nothing. */
function NoResults() {
	const { t } = useLingui();
	return (
		<DirectoryNotice
			title={t`We couldn't find the NGO you're looking for.`}
			body={t`Check the name is spelled correctly, or widen your search criteria.`}
		/>
	);
}

/**
 * What the section shows when the roster itself could not be loaded.
 *
 * A DIFFERENT message from `NoResults` on purpose: "we couldn't find that NGO" would be a lie about
 * the partners when the truth is that we failed to ask. `getPartnerNgos` no longer swallows its own
 * failures — that would cache an empty directory for an hour — so this is where they surface, and it
 * keeps them inside the section instead of taking the whole page to the route error boundary.
 */
function DirectoryUnavailable() {
	const { t } = useLingui();
	return <DirectoryNotice title={t`We couldn't load the list of NGOs.`} body={t`Please try again in a few minutes.`} />;
}

/**
 * "ONG que forman parte de Worldcoo" — the directory.
 *
 * The roster is grouped into the eight action-scope categories rather than listed flat, which is the
 * hand-off's whole organising idea: a visitor arrives looking for who works on a cause, not for an
 * alphabet. An NGO with several fields of work therefore appears in several rows, on purpose.
 *
 * Filter state is local rather than in the URL. The page is a marketing landing whose sections are
 * read in order, so a filtered directory is not a destination worth linking to — and putting it in
 * `validateSearch` would pull the schema into the route reference module, which is never code-split.
 */
function DirectoryBody() {
	const [filters, setFilters] = useState<NgoFilters>(EMPTY_FILTERS);
	const [openNgo, setOpenNgo] = useState<PartnerNgo | null>(null);
	const categoryLabels = useCategoryLabels();
	const { filtered, countryOptions, regionOptions } = usePartnerNgos(filters);

	// A category filter narrows the ROWS, not just the partners inside them. Filtering only the NGOs
	// left every other row standing — most partners work across several categories, so picking "Salud"
	// still drew Medioambiente, Emergencias and the rest, each holding whichever health NGOs happened
	// to also work there. Empty rows are dropped too: one would read as "we work with nobody on this".
	const rows = CATEGORY_ORDER.filter((category) => !filters.category || category === filters.category)
		.map((category) => ({ category, ngos: filtered.filter((ngo) => ngo.categories.includes(category)) }))
		.filter((row) => row.ngos.length > 0);

	return (
		<>
			<div className="mt-12">
				<NgoFiltersBar
					filters={filters}
					onChange={setFilters}
					countryOptions={countryOptions}
					regionOptions={regionOptions}
				/>
			</div>

			{rows.length === 0 ? (
				<NoResults />
			) : (
				<div className="mt-6 flex flex-col gap-6">
					{rows.map((row) => (
						<NgoCategoryRow
							key={row.category}
							category={row.category}
							label={categoryLabels[row.category]}
							ngos={row.ngos}
							onOpen={setOpenNgo}
							// Filtering to one category leaves the page mostly empty, so that row opens itself
							// rather than making the visitor take a second action to see what they filtered for.
							openByDefault={!!filters.category}
						/>
					))}
				</div>
			)}

			<NgoProjectsModal ngo={openNgo} onOpenChange={setOpenNgo} />
		</>
	);
}

/**
 * The section shell: the heading is static copy, so it renders whatever the roster does. Only the
 * part that needs data sits behind the boundaries — a failed fetch swaps the filters and rows for a
 * notice, and the visitor still sees what the section is.
 */
export function NgoDirectory() {
	const { t } = useLingui();
	const { reset } = useQueryErrorResetBoundary();

	return (
		<section className="bg-blush">
			<div className="mx-auto max-w-[1440px] px-6 py-16 md:py-[86px] xl:px-[149px]">
				<div className="flex flex-col items-center gap-2 text-center">
					<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`NGOs that are `}
						<Squiggle>{t`part of Worldcoo`}</Squiggle>
					</h2>
					<p className="text-base leading-[1.3] font-medium">
						{t`We have a network of more than 1,000 partner NGOs that make real impact possible.`}
					</p>
				</div>

				<ErrorBoundary onReset={reset} fallbackRender={() => <DirectoryUnavailable />}>
					<Suspense fallback={<div className="min-h-[600px]" />}>
						<DirectoryBody />
					</Suspense>
				</ErrorBoundary>
			</div>
		</section>
	);
}
